/**
 * React Query hooks for people, plus the pure view helpers the People
 * page and person card compose from (occasions about a person, their
 * documents, list-row labels). Mirrors the optimistic pattern used by
 * obligations so an inline person-create (from the PersonPicker) shows
 * up in the picker's own dropdown instantly.
 */

import type {
  DocumentDTO,
  ObligationDTO,
  PersonCreateInput,
  PersonDTO,
  PersonUpdateInput,
} from '@almanac/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { mutationKeys, queryKeys } from './queryClient.js';

export function usePeople() {
  return useQuery<PersonDTO[]>({
    queryKey: queryKeys.people,
    queryFn: api.listPeople,
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.createPerson,
    mutationFn: (input: PersonCreateInput & { id: string }) => api.createPerson(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.people });
      const previous = qc.getQueryData<PersonDTO[]>(queryKeys.people);
      const now = new Date().toISOString();
      const optimistic: PersonDTO = {
        id: input.id,
        displayName: input.displayName,
        relationship: input.relationship ?? null,
        relatedPersonIds: input.relatedPersonIds ?? [],
        notes: input.notes ?? null,
        household: input.household ?? false,
        avatarVersion: null,
        createdBy: 'me',
        createdAt: now,
        updatedAt: now,
      };
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        [...(prev ?? []), optimistic].sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        ),
      );
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.people, ctx.previous);
    },
    onSuccess: (server) => {
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).map((p) => (p.id === server.id ? server : p)),
      );
    },
  });
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: PersonUpdateInput }) =>
      api.updatePerson(vars.id, vars.input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.people });
      const previous = qc.getQueryData<PersonDTO[]>(queryKeys.people);
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).map((p) =>
          p.id === id
            ? {
                ...p,
                displayName: input.displayName ?? p.displayName,
                relationship:
                  input.relationship === undefined
                    ? p.relationship
                    : (input.relationship ?? null),
                relatedPersonIds:
                  input.relatedPersonIds === undefined
                    ? p.relatedPersonIds
                    : input.relatedPersonIds,
                notes: input.notes === undefined ? p.notes : (input.notes ?? null),
                household: input.household === undefined ? p.household : input.household,
                updatedAt: new Date().toISOString(),
              }
            : p,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.people, ctx.previous);
    },
    onSuccess: (server) => {
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).map((p) => (p.id === server.id ? server : p)),
      );
    },
  });
}

export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deletePerson(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.people });
      const previous = qc.getQueryData<PersonDTO[]>(queryKeys.people);
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).filter((p) => p.id !== id),
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.people, ctx.previous);
    },
  });
}

// ── View helpers (pure) ──────────────────────────────────────────────

/** Two-letter avatar initials for a person. */
export function personInitials(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Occasions linked to a person, soonest next occurrence first. */
export function personOccasions(
  obligations: ObligationDTO[],
  personId: string,
): ObligationDTO[] {
  return obligations
    .filter((o) => o.kind === 'occasion' && o.personIds.includes(personId))
    .sort((a, b) => (a.nextDueDate ?? a.dueDate).localeCompare(b.nextDueDate ?? b.dueDate));
}

/** Documents that are about a person (pertainsTo contains them). */
export function personDocuments(documents: DocumentDTO[], personId: string): DocumentDTO[] {
  return documents.filter((d) => (d.pertainsTo ?? []).includes(personId));
}

/** The years an occasion completes at its next occurrence (age for a
 *  birthday, married-years for an anniversary), or null if unknown. */
function occasionYears(o: ObligationDTO): number | null {
  const nextYear = o.nextDueDate ? Number(o.nextDueDate.slice(0, 4)) : null;
  return nextYear && o.anchorYear ? nextYear - o.anchorYear : null;
}

/**
 * Full description for a person's occasions list —
 * "Birthday · 29 November 2023 · turns 3 next time".
 */
export function describeOccasion(o: ObligationDTO): string {
  const kind =
    o.occasionType === 'anniversary'
      ? o.occasionLabel?.trim() || 'Anniversary'
      : 'Birthday';
  const dueDate = (o.dueDate ?? '').slice(0, 10);
  const d = dueDate ? new Date(`${dueDate}T00:00:00`) : null;
  const dateLabel =
    d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
      : dueDate;
  const years = occasionYears(o);
  if (years && years > 0) {
    if (o.occasionType === 'anniversary') {
      return `${kind} · ${dateLabel} · ${years} ${years === 1 ? 'year' : 'years'} this time`;
    }
    return `${kind} · ${dateLabel} · turns ${years} next time`;
  }
  return `${kind} · ${dateLabel}`;
}

/**
 * Compact next-occasion label for a People list row —
 * "turns 3 on 29 Nov", "birthday 14 Mar", "anniversary 12 Jun".
 * (The row's meta line renders uppercase; keep the copy lowercase.)
 */
export function shortOccasionLabel(o: ObligationDTO): string {
  const next = (o.nextDueDate ?? o.dueDate).slice(0, 10);
  const d = new Date(`${next}T00:00:00`);
  const when = Number.isNaN(d.getTime())
    ? next
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (o.occasionType === 'anniversary') {
    const label = (o.occasionLabel?.trim() || 'anniversary').toLowerCase();
    return `${label} ${when}`;
  }
  const years = occasionYears(o);
  if (years && years > 0) return `turns ${years} on ${when}`;
  return `birthday ${when}`;
}

/**
 * Household membership: the explicit per-person flag (toggled in the
 * person editor; migration 0019 seeded it from the old relationship
 * heuristic), plus the signed-in user's own linked record — you are
 * always part of your household. Groups first in the People list and
 * leads every person picker.
 */
export function isHouseholdMember(p: PersonDTO, mePersonId: string | null | undefined): boolean {
  return p.household || p.id === mePersonId;
}

/** Set (replace) a person's photo. The cache takes the server's person
 *  row on success — its new `avatarVersion` busts the img URL. */
export function useSetPersonAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; blob: Blob }) =>
      api.uploadPersonAvatar(vars.id, vars.blob),
    onSuccess: (server) => {
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).map((p) => (p.id === server.id ? server : p)),
      );
    },
  });
}

/** Remove a person's photo — back to initials. */
export function useRemovePersonAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.removePersonAvatar(id),
    onSuccess: (server) => {
      qc.setQueryData<PersonDTO[]>(queryKeys.people, (prev) =>
        (prev ?? []).map((p) => (p.id === server.id ? server : p)),
      );
    },
  });
}
